import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAPI } from '../../services/apiService';
import { toast } from 'react-hot-toast';
import { Pencil, ChevronRight, Plus, X } from 'lucide-react';

const EditableCell = ({ value, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        type="text"
        className="px-2 py-1 border rounded-md w-full"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyPress={(e) => e.key === 'Enter' && handleSave()}
        autoFocus
      />
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <span>{value}</span>
      <button 
        onClick={() => setIsEditing(true)}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <Pencil className="h-4 w-4 text-gray-500" />
      </button>
    </div>
  );
};

const InvitePhysicianModal = ({ isOpen, onClose, onSubmit, specialties }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    specialty_id: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ name: '', email: '', specialty_id: '' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Invite New Physician</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Specialty</label>
              <select
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                value={formData.specialty_id}
                onChange={(e) => setFormData({ ...formData, specialty_id: e.target.value })}
              >
                <option value="">Select Specialty</option>
                {specialties.map(specialty => (
                  <option key={specialty.specialty_id} value={specialty.specialty_id}>
                    {specialty.specialty_name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm italic text-gray-500">
                Please choose specialty carefully, as changing specialty will wipe out template settings.
              </p>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Send Invitation
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const PhysicianManagement = () => {
  const api = useAPI();
  const navigate = useNavigate();
  const location = useLocation();
  const [physicians, setPhysicians] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  useEffect(() => {
    fetchPhysicians();
    fetchSpecialties();
  }, []);

  const fetchPhysicians = async () => {
    try {
      const { data } = await api.get('/physicians');
      setPhysicians(data || []);
    } catch (err) {
      toast.error('Failed to load physicians');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecialties = async () => {
    try {
      const { data } = await api.get('/specialties');
      setSpecialties(data || []);
    } catch (err) {
      toast.error('Failed to load specialties');
    }
  };

  const handleNameChange = async (userId, newName) => {
    try {
      await api.put(`/physicians/${userId}`, { name: newName });
      await fetchPhysicians();
      toast.success('Name updated');
    } catch (err) {
      toast.error('Failed to update name');
    }
  };

  const handleSpecialtyChange = async (userId, specialtyId) => {
    try {
      await api.put(`/physicians/${userId}`, { specialty_id: specialtyId });
      await fetchPhysicians();
      toast.success('Specialty updated');
    } catch (err) {
      toast.error('Failed to update specialty');
    }
  };

  const handleInviteSubmit = async (formData) => {
    try {
      await api.post('/physicians/invite', formData);
      setIsInviteModalOpen(false);
      toast.success('Invitation sent successfully');
      await fetchPhysicians();
    } catch (err) {
      toast.error('Failed to send invitation');
    }
  };

  const handleEditTemplate = (physician) => {
    navigate(`/templates/${physician.template_id || 'new'}`, {
      state: { 
        physicianId: physician.user_id,
        physicianName: physician.name
      }
    });
  };

  const getNavigationLinks = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const links = [];
    
    if (pathSegments.includes('templates')) {
      const templateId = pathSegments[pathSegments.indexOf('templates') + 1];
      links.push({
        to: `/templates/${templateId}`,
        label: 'Review Preferences'
      });
    }

    if (pathSegments.includes('document-types')) {
      const templateId = pathSegments[pathSegments.indexOf('templates') + 1];
      const docType = pathSegments[pathSegments.indexOf('document-types') + 1];
      links.push({
        to: `/templates/${templateId}/document-types/${docType}`,
        label: 'Billing Report'
      });
    }

    return links;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-4 text-sm text-gray-600">
        {getNavigationLinks().map((link, index) => (
          <React.Fragment key={link.to}>
            {index > 0 && <span className="mx-2">&gt;</span>}
            <button
              onClick={() => navigate(link.to)}
              className="hover:text-blue-600"
            >
              {link.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Physician Management</h2>
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Invite Physician</span>
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Specialty
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {physicians.map(physician => (
                <tr key={physician.user_id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <EditableCell
                      value={physician.name}
                      onSave={(newName) => handleNameChange(physician.user_id, newName)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {physician.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      className="px-2 py-1 border rounded-md w-[200px]"
                      value={physician.specialty_id || ''}
                      onChange={(e) => handleSpecialtyChange(physician.user_id, e.target.value)}
                    >
                      <option value="">Select Specialty</option>
                      {specialties.map(specialty => (
                        <option key={specialty.specialty_id} value={specialty.specialty_id}>
                          {specialty.specialty_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleEditTemplate(physician)}
                      className="px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 flex items-center justify-between w-[160px]"
                    >
                      <span>{physician.template_id ? 'Edit Template' : 'Create Template'}</span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <InvitePhysicianModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSubmit={handleInviteSubmit}
        specialties={specialties}
      />
    </div>
  );
};

export default PhysicianManagement; 